import Phaser from 'phaser';
import { SubscriptionManager } from '../core/SignalSubscription';
import {
  createLifecycleSignals,
  type LifecycleSignals,
} from '../signals/lifecycle';
import { createPlayerSignals, type PlayerSignals } from '../signals/player';
import { createScoreSignals, type ScoreSignals } from '../signals/score';
import { createUISignals, type UISignals } from '../signals/ui';
import {
  createSessionSignals,
  type SessionSignals,
} from '../signals/session';
import { createAssetSignals, type AssetSignals } from '../signals/assets';
import { Signal } from '../core/Signal';
import { isSignalLoggingEnabled } from '../utils/env';

export interface GlobalSignals {
  lifecycle: LifecycleSignals;
  player: PlayerSignals;
  score: ScoreSignals;
  ui: UISignals;
  session: SessionSignals;
  assets: AssetSignals;
}

export class SignalsPlugin extends Phaser.Plugins.ScenePlugin {
  public readonly global: GlobalSignals;

  // Using `any` here because this is a heterogeneous collection where signals
  // of different types are stored and retrieved with user-specified type parameters
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private sceneSignals = new Map<string, Signal<any>>();

  private subscriptions = new SubscriptionManager();

  private debugMode: boolean = false;

  constructor(
    scene: Phaser.Scene,
    pluginManager: Phaser.Plugins.PluginManager
  ) {
    super(scene, pluginManager, 'signals');

    // Check LOG_SIGNALS environment variable for signal logging
    this.debugMode = isSignalLoggingEnabled();

    if (!scene.game.registry.get('__signals_global__')) {
      const globalSignals: GlobalSignals = {
        lifecycle: createLifecycleSignals(this.debugMode),
        player: createPlayerSignals(this.debugMode),
        score: createScoreSignals(this.debugMode),
        ui: createUISignals(this.debugMode),
        session: createSessionSignals(this.debugMode),
        assets: createAssetSignals(this.debugMode),
      };
      scene.game.registry.set('__signals_global__', globalSignals);
    }

    this.global = scene.game.registry.get('__signals_global__');
  }

  create<T>(name: string): Signal<T> {
    if (this.sceneSignals.has(name)) {
      console.warn(`[SignalsPlugin] Signal "${name}" already exists`);
      return this.sceneSignals.get(name)!;
    }

    const signal = new Signal<T>(`scene.${name}`, this.debugMode);
    this.sceneSignals.set(name, signal);
    return signal;
  }

  get<T>(name: string): Signal<T> | undefined {
    return this.sceneSignals.get(name);
  }

  getOrCreate<T>(name: string): Signal<T> {
    return this.get<T>(name) || this.create<T>(name);
  }

  remove(name: string): void {
    const signal = this.sceneSignals.get(name);
    if (signal) {
      signal.clear();
      this.sceneSignals.delete(name);
    }
  }

  setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }

  shutdown(): void {
    this.subscriptions.unsubscribeAll();
    this.sceneSignals.forEach((signal) => signal.clear());
    this.sceneSignals.clear();
  }

  destroy(): void {
    this.shutdown();
    super.destroy();
  }
}
